import styled from '@emotion/styled';
import React, { FC, memo, useEffect, useState } from 'react';
import DateInput from '~ComponentsImproved/DateInput';
import { useDebounce, useI18n } from '~Hooks';
import { Box, Input } from '~Styles';
import AdvancedFilters, { AdvancedFilterValues } from './AdvancedFilters';
import { useStorage } from '~Components/Storage';

/** Standard debounce delay so we don't fire a query/URL update on every keystroke */
const SEARCH_DEBOUNCE_MS = 300;

type SearchParamKey = 'searchterm' | 'searchsum' | 'searchtip';

/**
 * Keeps an instant local value for a search input while only propagating the
 * debounced value upward. Also re-syncs when the external value changes (e.g. filters cleared elsewhere).
 */
const useDebouncedSearchField = (
  externalValue: string,
  key: SearchParamKey,
  onChange: (key: SearchParamKey, value: string) => void,
) => {
  const [value, setValue] = useState(externalValue);
  const debouncedValue = useDebounce(value, SEARCH_DEBOUNCE_MS);

  /** Pull in external changes (e.g. reset) without firing onChange */
  useEffect(() => {
    setValue(externalValue);
  }, [externalValue]);

  /** Propagate up only once typing settles, and only if it actually changed */
  useEffect(() => {
    if (debouncedValue !== externalValue) {
      onChange(key, debouncedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  return [value, setValue] as const;
};

/** Search inputs share the row equally so they cover the full available width */
const SearchInput = styled(Input)`
  flex: 1;
  min-width: 0;
`;

/** The tip search input is hidden on narrower screens to keep the row readable */
const TipSearchInput = styled(SearchInput)`
  @media (max-width: 1380px) {
    display: none;
  }
`;

interface Props {
  searchTerm: string;
  searchSum: string;
  searchTip: string;
  selectedDate: string | null;
  advancedFilters: AdvancedFilterValues;
  onAdvancedFiltersChange: (filters: AdvancedFilterValues) => void;
  /** Update one of the simple search params (search term / sum / tip) */
  onSearchParamChange: (key: 'searchterm' | 'searchsum' | 'searchtip', value: string) => void;
  /** Set or clear (null) the date filter */
  onDateChange: (date: Date | null) => void;
}

/**
 * Search/filter controls for the history list.
 * Receives plain values + stable callbacks (no router hooks) so it can be memoized
 * and won't re-render when unrelated state like the selected order changes.
 */
const FilterSection: FC<Props> = ({
  searchTerm,
  searchSum,
  searchTip,
  selectedDate,
  advancedFilters,
  onAdvancedFiltersChange,
  onSearchParamChange,
  onDateChange,
}) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();

  /** Local input values update instantly; the debounced value is what hits the URL/query */
  const [termValue, setTermValue] = useDebouncedSearchField(
    searchTerm,
    'searchterm',
    onSearchParamChange,
  );
  const [sumValue, setSumValue] = useDebouncedSearchField(
    searchSum,
    'searchsum',
    onSearchParamChange,
  );
  const [tipValue, setTipValue] = useDebouncedSearchField(
    searchTip,
    'searchtip',
    onSearchParamChange,
  );

  return (
    <Box display="flex" flexDirection="column" gap="xs" marginBottom="small">
      <Box display="flex" gap="xs" fluid>
        <SearchInput
          dark
          type="text"
          value={termValue}
          placeholder={i18n('Handy.History.Search.Placeholder')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTermValue(e.target.value)}
        />
        <SearchInput
          dark
          type="text"
          value={sumValue}
          placeholder={i18n('Handy.History.Search.Sum.Placeholder')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSumValue(e.target.value)}
        />
        {/* Tip filter is only relevant when tipping is enabled, and hidden under 1380px */}
        {storage.enableTipping && (
          <TipSearchInput
            dark
            type="text"
            value={tipValue}
            placeholder={i18n('Handy.History.Search.Tip.Placeholder')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTipValue(e.target.value)}
          />
        )}

        <AdvancedFilters filters={advancedFilters} onChange={onAdvancedFiltersChange} />
      </Box>
      <DateInput
        selectedDate={selectedDate ? new Date(selectedDate) : null}
        setSelectedDate={(date) => onDateChange(date instanceof Date ? date : null)}
        /** Orders can't exist in the future, so block selecting any day after today */
        maxDate={new Date()}
        isDark
      />
    </Box>
  );
};

export default memo(FilterSection);
